// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

// Format date helper
const formatDate = (timestamp) => {
  if (!timestamp) return '';

  const now = Math.floor(Date.now() / 1000);
  const diffSecs = now - timestamp;
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffSecs / 3600);
  const diffDays = Math.floor(diffSecs / 86400);

  if (diffSecs < 0 || diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return new Date(timestamp * 1000).toLocaleDateString();
};

export const SearchChatsModal = ({
  isOpen,
  onClose,
  onSelectThread,
  apiUrl
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      setHasSearched(true);

      try {
        const token = localStorage.getItem('cme_token');
        const response = await fetch(`${apiUrl}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'threads.search',
            params: {
              query: query.trim(),
              limit: 20
            }
          })
        });

        if (response.ok) {
          const result = await response.json();
          setResults(result.data || []);
        } else {
          console.error('Search failed');
          setResults([]);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, apiUrl]);

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
    onClose();
  };

  const handleSelectThread = async (threadId) => {
    handleClose();
    await onSelectThread(threadId);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
          mt: 8,
        },
      }}
    >
      {/* Search Input */}
      <Box sx={{ p: 2 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search Chats..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClose} size="small" edge="end">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 6 },
          }}
        />
      </Box>

      {/* Search Results */}
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pb: 2 }}>
        {!hasSearched && (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary', py: 8 }}
          >
            Start typing to search your chats...
          </Typography>
        )}

        {isSearching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {hasSearched && !isSearching && results.length === 0 && (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary', py: 8 }}
          >
            No chats found
          </Typography>
        )}

        {results.length > 0 && (
          <List disablePadding>
            {results.map((thread) => (
              <ListItemButton
                key={thread.id}
                onClick={() => handleSelectThread(thread.id)}
                sx={{ px: 2, py: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ChatBubbleOutlineIcon sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText
                  primary={thread.title || 'Untitled Chat'}
                  secondary={formatDate(thread.created_timestamp || thread.updated_timestamp)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
};
